import { generateHTML } from '@tiptap/html';
import DOMPurify from 'isomorphic-dompurify';

import { extensions } from './extensions';

import type { ArticleDoc, AssetMap } from './doc';

// The schema itself. Anything outside these sets is dropped before rendering, so
// an unknown node type never reaches generateHTML (which would throw).
const KNOWN_NODES = new Set([
  'doc',
  'paragraph',
  'text',
  'heading',
  'hardBreak',
  'blockquote',
  'bulletList',
  'orderedList',
  'listItem',
  'horizontalRule',
  'pullQuote',
  // Spec 39: without this, every image node is dropped before rendering — the
  // single easiest way to ship inline images silently broken.
  'image',
]);
const KNOWN_MARKS = new Set(['bold', 'italic', 'link']);

/**
 * Resolve an image node against the asset map, attaching transient src/alt/
 * caption/width/height for renderHTML. A missing asset (or no assetId) drops the
 * figure and warns — a missing figure beats a broken <img> on a published page,
 * but a silent drop is how the feature rots, so it must be diagnosable.
 */
function resolveImage(attrs: unknown, assets: AssetMap): Record<string, unknown> | null {
  const assetId = (attrs as { assetId?: unknown } | null | undefined)?.assetId;
  if (typeof assetId !== 'string') {
    console.warn('[renderDocument] image node without an assetId — figure dropped');
    return null;
  }
  const asset = assets[assetId];
  if (!asset) {
    console.warn(`[renderDocument] unresolved assetId "${assetId}" — figure dropped`);
    return null;
  }
  return {
    type: 'image',
    attrs: {
      assetId,
      src: asset.publicUrl,
      alt: asset.altText,
      caption: asset.caption,
      width: asset.width,
      height: asset.height,
    },
  };
}

/** Recursively drop unknown node/mark types so one bad node can't crash the doc. */
function sanitizeNode(node: unknown, assets: AssetMap): Record<string, unknown> | null {
  if (!node || typeof node !== 'object') return null;
  const n = node as Record<string, unknown>;
  if (typeof n.type !== 'string' || !KNOWN_NODES.has(n.type)) return null;
  if (n.type === 'image') return resolveImage(n.attrs, assets);
  const out: Record<string, unknown> = { type: n.type };
  if (typeof n.text === 'string') out.text = n.text;
  if (n.attrs && typeof n.attrs === 'object') out.attrs = n.attrs;
  if (Array.isArray(n.marks)) {
    const marks = n.marks.filter(
      (m) => m && typeof m === 'object' && KNOWN_MARKS.has(String((m as { type?: string }).type)),
    );
    if (marks.length > 0) out.marks = marks;
  }
  if (Array.isArray(n.content)) {
    out.content = n.content
      .map((c) => sanitizeNode(c, assets))
      .filter((c): c is Record<string, unknown> => c !== null);
  }
  return out;
}

// Belt-and-braces: the schema already means there's no <script> node to produce.
// This strips a javascript:/data: href at render (the input-side guard is the
// Link extension's isAllowedUri) and any stray attribute.
const SANITIZE_OPTIONS = {
  USE_PROFILES: { html: true },
  FORBID_TAGS: ['style', 'script', 'iframe', 'object', 'embed', 'form'],
  FORBID_ATTR: ['style', 'onerror', 'onload', 'onclick'],
};

/**
 * Render a stored ProseMirror document to sanitized HTML. Synchronous and pure —
 * runs in Node during the marketing SSG build. `assets` resolves image nodes by
 * id (Spec 39); pass collectAssetIds(doc) → fetched rows. An empty map drops all
 * figures rather than failing.
 */
export function renderDocument(doc: ArticleDoc | null | undefined, assets: AssetMap = {}): string {
  if (!doc || typeof doc !== 'object') return '';
  const clean = sanitizeNode(doc, assets);
  if (!clean || clean.type !== 'doc') return '';
  try {
    const html = generateHTML(clean as ArticleDoc, extensions);
    return DOMPurify.sanitize(html, SANITIZE_OPTIONS) as unknown as string;
  } catch {
    return '';
  }
}
